import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Modal, {
  ModalProps,
  ModalRef,
} from '@/components/@IntranetLibrary/Modal';
import { useTranslation } from 'react-i18next';
import {
  CreateBoardSlide,
  CreateBoardSlideItem,
} from '@/components/Board/CreateBoardModal/styles';
import useBoardFormOptions from '@/hooks/board/useBoardFormOptions';
import {
  BoardCommentSection,
  BoardDetailData,
  DefaultDeployMessage,
  DeployStepName,
  getDeployFailureMessage,
  getDeployStatusMessage,
  isDeployFailed,
  isDeploySuccess,
} from '@/types/board';
import useLayoutSize from '@/hooks/useLayoutSize';
import { LayoutType } from '@/constant/layout';
import BoardDetail from '@/components/Board/BoardDetail';
import { useDialog } from '@/hooks/global-tools';
import NewBoardForm from '@/components/Board/CreateBoardModal/BoardForm/NewBoardForm';
import CreateBoardFooter from './CreateBoardFooter';
import {
  FormAction,
  useBoardFormActionProps,
} from '@/hooks/board/useBoardFormAction';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { BoardFormData } from '@/hooks/board/useBoardForm';
import EditBoardForm from './BoardForm/EditBoardForm';
import DeployProgressStepper from './DeployProgressStepper';
import DeployOverlay from './DeployOverlay';
import { useBoardDeploy, useGetBoardDeployStatus } from '@/hooks/apiHooks';
import { useFetchDeployUrl } from '@/hooks/board/useFetchDeployUrl';

export const CreateBoardStepValues = {
  WriteForm: 0,
  Preview: 1,
} as const;
export type CreateBoardStep =
  (typeof CreateBoardStepValues)[keyof typeof CreateBoardStepValues];

export type BoardFormRef = {
  action: FormAction & {
    reset?: () => void;
    getCodeZipFileStatus?: () => 'new' | 'deleted' | 'default' | undefined;
    getValues?: UseFormGetValues<BoardFormData>;
    setValue?: UseFormSetValue<BoardFormData>;
  };
};

type CreateBoardModalProps = {
  readonly documentId?: string; // 수정용
  readonly categoryId?: string; // 작성용
  readonly categoryGroupId?: string; // 작성용
} & Pick<useBoardFormActionProps, 'onSubmit'> &
  Pick<ModalProps, 'handleClose'>;

const CreateBoardModal = forwardRef<ModalRef, CreateBoardModalProps>(
  (props, ref) => {
    const {
      documentId,
      categoryId,
      categoryGroupId,
      handleClose: _handleClose,
      onSubmit,
    } = props;
    const isNew = !documentId;

    const { t } = useTranslation();
    const { isMobile, isSmallDesktop } = useLayoutSize(
      LayoutType.BOARD_DETAIL_MODAL,
    );
    const dialog = useDialog();

    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState(
      isNew ? t('게시글 작성') : t('게시글 수정'),
    );
    const [isFirstStep, setIsFirstStep] = useState(false);
    const [step, setStep] = useState<CreateBoardStep>(
      CreateBoardStepValues.WriteForm,
    );
    const [disabledSubmit, setDisabledSubmit] = useState<boolean>(false);
    const [disabledSave, setDisabledSave] = useState<boolean>(false);
    const [footerDescription, setFooterDescription] = useState<string>('');

    const options = useBoardFormOptions();
    const defaultCategory =
      options._categories &&
      (categoryGroupId
        ? options._categories.find((item) => item.groupId === categoryGroupId)
            ?.id
        : categoryId);
    const [isFormDirty, setIsFormDirty] = useState(false);

    const formRef = useRef<BoardFormRef>(null);
    const formAction = formRef.current ? formRef.current.action : null;
    const [previewData, setPreviewData] = useState<BoardDetailData>();
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [isRowDirection, setIsRowDirection] = useState<boolean>(false);
    const previewScrollRef = useRef(null);
    const [isDeploying, setIsDeploying] = useState<boolean>(false);
    const [isDeployStepperVisible, setIsDeployStepperVisible] =
      useState<boolean>(false);
    const [isDeployReplayMode, setIsDeployReplayMode] =
      useState<boolean>(false);
    const [isSimpleLoading, setIsSimpleLoading] = useState<boolean>(false);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingStartTimeRef = useRef<number | null>(null);
    const [formType, setFormType] = useState<'editor' | 'codeUpload'>('editor');
    const [deployStatus, setDeployStatus] = useState<{
      step: DeployStepName | null;
      status: 'pending' | 'success' | 'failed' | 'warning';
      message: string;
    }>({
      step: null,
      status: 'pending',
      message: t(DefaultDeployMessage),
    });
    const { fetchDeployUrl, errorDetail } = useFetchDeployUrl();
    const deployAnimationTimeoutsRef = useRef<NodeJS.Timeout[]>([]);

    const closeMessage = isNew
      ? '작성 화면을 닫으시겠어요?\n임시저장 되지 않은 내용은 사라집니다.'
      : '작성 화면을 나가시겠어요?\n수정한 내용은 사라집니다.';

    useEffect(() => {
      if (step === CreateBoardStepValues.WriteForm) {
        setTitle(isNew ? t('게시글 작성') : t('게시글 수정'));
        setIsFirstStep(true);
      } else if (step === CreateBoardStepValues.Preview) {
        setTitle(t('미리 보기'));
        setIsFirstStep(false);
      }
    }, [isOpen, step]);

    useEffect(() => {
      if (!previewData) return;

      setIsRowDirection(
        !isMobile &&
          !isSmallDesktop &&
          previewData.config.commentSection === BoardCommentSection.RIGHT,
      );
    }, [previewData]);

    const handleBeforeUnload = useCallback(
      (event: BeforeUnloadEvent) => {
        event.preventDefault();
        if (isFormDirty) {
          event.returnValue = closeMessage;
          return closeMessage;
        }
      },
      [isFormDirty],
    );

    useEffect(() => {
      if (!isOpen) return;

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, [isOpen, isFormDirty]);

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    const clearDeployAnimationTimeouts = () => {
      deployAnimationTimeoutsRef.current.forEach(clearTimeout);
      deployAnimationTimeoutsRef.current = [];
    };

    // previewUrl 설정 시 codeZipFile status를 default로 변경
    const updatePreviewUrl = (url: string) => {
      setPreviewUrl(url);

      if (url && formRef.current?.action) {
        const { getValues, setValue } = formRef.current.action;
        if (!getValues || !setValue) return;

        const values = getValues();
        const currentCodeZipFile = values.codeZipFile;
        if (currentCodeZipFile && currentCodeZipFile.status === 'new') {
          setValue('codeZipFile' as const, {
            ...currentCodeZipFile,
            status: 'default',
          });
        }
      }
    };

    const startPolling = (documentId: string) => {
      stopPolling();
      pollingStartTimeRef.current = Date.now();

      pollingIntervalRef.current = setInterval(async () => {
        try {
          const result = await useGetBoardDeployStatus(documentId);

          // 상태 업데이트 (step, status, message)
          setDeployStatus({
            step: result.step as DeployStepName,
            status: result.status as
              | 'pending'
              | 'success'
              | 'failed'
              | 'warning',
            message: t(
              isDeployFailed(result)
                ? getDeployFailureMessage(result)
                : getDeployStatusMessage(result),
            ),
          });

          if (isDeploySuccess(result)) {
            // step: "deploy", status: "success"
            stopPolling();
            setIsDeploying(false);
            pollingStartTimeRef.current = null;
            if (result.url) {
              const completeTimeout = setTimeout(() => {
                updatePreviewUrl(result.url ?? '');
                setStep(CreateBoardStepValues.Preview);
                setIsDeployStepperVisible(false);
                clearDeployAnimationTimeouts();
              }, 1400);

              deployAnimationTimeoutsRef.current.push(completeTimeout);
            } else {
              setIsDeployStepperVisible(false);
              dialog.show({
                message: t(
                  '배포는 완료되었으나 미리보기 URL을 받아오지 못했습니다.',
                ),
              });
            }
          } else if (isDeployFailed(result)) {
            // 실패 처리
            stopPolling();
            setIsDeploying(false);
            pollingStartTimeRef.current = null;
          }
          // isDeployProcessing(result) 일 때는 계속 폴링
        } catch (error) {
          stopPolling();
          setIsDeploying(false);
          pollingStartTimeRef.current = null;
          dialog.show({
            message: t('배포 상태 확인 중 오류가 발생했어요.'),
          });
        }
      }, 1000);
    };

    const startDeployProcess = async (documentId: string) => {
      try {
        // 새로운 배포 시작 전 상태 초기화
        clearDeployAnimationTimeouts();
        setDeployStatus({
          step: null,
          status: 'pending',
          message: t(DefaultDeployMessage),
        });
        setIsDeployStepperVisible(true);
        setIsDeployReplayMode(false);
        setIsDeploying(true);
        await useBoardDeploy(documentId);
        startPolling(documentId);
      } catch (error) {
        setIsDeploying(false);
        setIsDeployStepperVisible(false);
        dialog.show({
          message: t('배포 중 오류가 발생했어요.'),
        });
      }
    };

    const showExistingDeploySuccess = (url: string) => {
      clearDeployAnimationTimeouts();
      setDeployStatus({
        step: null,
        status: 'pending',
        message: t(DefaultDeployMessage),
      });
      setIsDeployStepperVisible(true);
      setIsDeployReplayMode(true);
      setIsDeploying(true);

      const steps: DeployStepName[] = [
        'codebuild_start',
        'security_scan',
        'build',
        'deploy',
      ];

      steps.forEach((deployStep, index) => {
        const stepStartDelay = 1200 * index + 60;
        const stepCompleteDelay = stepStartDelay + 420;

        const startTimeout = setTimeout(() => {
          setDeployStatus({
            step: deployStep,
            status: 'pending',
            message: t(DefaultDeployMessage),
          });
        }, stepStartDelay);

        const completeTimeout = setTimeout(() => {
          setDeployStatus({
            step: deployStep,
            status: 'success',
            message: t(
              getDeployStatusMessage({
                step: deployStep,
                status: 'success',
              }),
            ),
          });
        }, stepCompleteDelay);

        deployAnimationTimeoutsRef.current.push(startTimeout, completeTimeout);
      });

      const completeTimeout = setTimeout(() => {
        updatePreviewUrl(url);
        setStep(CreateBoardStepValues.Preview);
        setIsDeploying(false);
        setIsDeployStepperVisible(false);
        setIsDeployReplayMode(false);
        clearDeployAnimationTimeouts();
      }, 1200 * steps.length + 1400);

      deployAnimationTimeoutsRef.current.push(completeTimeout);
    };

    useEffect(() => {
      return () => {
        stopPolling();
        clearDeployAnimationTimeouts();
      };
    }, []);

    const handlePreviewForCodeUpload = async () => {
      if (!formRef.current) return;

      const { validateStartDate, getPreviewData, save, getCodeZipFileStatus } =
        formRef.current.action;

      if (!validateStartDate()) return;

      const previewDataTemp = getPreviewData(options._categories);
      setPreviewData(previewDataTemp);

      // 파일 상태를 확인하여 배포 여부 결정
      const codeZipFileStatus = getCodeZipFileStatus?.();
      const isFileChanged = codeZipFileStatus === 'new';

      // 게시글 내용을 저장 (새 글이든 수정이든 항상 저장)
      const deployDocumentId = await save(true);

      if (!deployDocumentId) {
        dialog.show({
          message: t('저장에 실패했습니다. 다시 시도해주세요.'),
        });
        return;
      }

      // 파일이 변경된 경우에만 배포 프로세스 시작
      if (isFileChanged) {
        // 새 배포 시작 시 이전 URL 초기화
        setPreviewUrl('');
        await startDeployProcess(deployDocumentId);
      } else {
        // 파일 변경이 없으면 기존 배포 URL 사용
        setDeployStatus({
          step: null,
          status: 'pending',
          message: t(DefaultDeployMessage),
        });
        setIsDeployStepperVisible(true);
        setIsDeployReplayMode(true);
        setIsDeploying(true);

        const result = await fetchDeployUrl(deployDocumentId);
        if (result.success) {
          showExistingDeploySuccess(result.url ?? '');
        } else {
          const response = result.response;
          const failureMessage =
            response && isDeployFailed(response)
              ? t(getDeployFailureMessage(response))
              : `${t('배포 정보를 불러올 수 없어요.')}${
                  result.errorDetail || errorDetail
                    ? `\n${t(result.errorDetail || errorDetail)}`
                    : ''
                }`;
          setIsDeploying(false);
          setDeployStatus({
            step: (response?.step ?? 'codebuild_start') as DeployStepName,
            status: 'failed',
            message: failureMessage,
          });
        }
      }
    };

    const changeStep = async (value: CreateBoardStep) => {
      switch (value) {
        case CreateBoardStepValues.WriteForm:
          setStep(value);
          // previewUrl은 유지 - 파일 변경 시에만 초기화됨
          break;
        case CreateBoardStepValues.Preview:
          if (formRef.current) {
            const { validateStartDate, getPreviewData } =
              formRef.current.action;
            if (validateStartDate()) {
              const previewDataTemp = getPreviewData(options._categories);
              setPreviewData(previewDataTemp);
              setStep(value);
            }
          }
          break;
      }
    };

    const close = () => {
      stopPolling();
      clearDeployAnimationTimeouts();
      setIsDeploying(false);
      setIsDeployStepperVisible(false);
      setIsDeployReplayMode(false);
      setIsSimpleLoading(false);
      setPreviewUrl('');
      setDeployStatus({
        step: null,
        status: 'pending',
        message: t(DefaultDeployMessage),
      });
      formAction?.reset?.();
      _handleClose?.();
      setStep(CreateBoardStepValues.WriteForm);
      setDisabledSubmit(isNew);
      setFooterDescription('');
      setIsOpen(false);
    };

    const handleClose = () => {
      dialog.show({
        type: 'confirm',
        message: closeMessage,
        confirmButtonText: '나가기',
        onConfirm: close,
      });
    };

    return (
      <Modal
        ref={ref}
        title={title}
        isFixedHeight
        isExpandable
        isShowBackButton={!isFirstStep}
        handleBack={() => changeStep(CreateBoardStepValues.WriteForm)}
        availableAutoClose={false}
        onOpen={() => setIsOpen(true)}
        handleClose={handleClose}
        footerContent={
          <CreateBoardFooter
            step={step}
            setStep={changeStep}
            isAvailableSave={isNew}
            disabledSubmit={disabledSubmit}
            disabledSave={disabledSave}
            description={footerDescription}
            formType={formType}
            isDeploying={isDeploying}
            handlePreviewForCodeUpload={handlePreviewForCodeUpload}
            handleSave={async () => {
              await formAction?.save?.(true, true);
            }}
            handleSubmit={() => {
              formAction?.submit();
            }}
          />
        }
        overlayContent={
          <>
            <DeployProgressStepper
              isVisible={isDeployStepperVisible}
              currentStep={deployStatus.step}
              currentStatus={deployStatus.status}
              serverMessage={deployStatus.message}
              onCloseError={() => {
                setIsDeployStepperVisible(false);
                setIsDeployReplayMode(false);
              }}
              advanceOnSuccess={!isDeployReplayMode}
              startFirstStepOnMount={!isDeployReplayMode}
              compactInProgress={isDeployReplayMode}
              flushWrapper={formType === 'codeUpload'}
            />
            <DeployOverlay
              isVisible={isSimpleLoading}
              message={t('배포 정보를 불러오는 중...')}
            />
          </>
        }
        sx={{ '.modal-body': { overflow: 'hidden' } }}
      >
        <>
          <CreateBoardSlide
            sx={{ transform: `translateX(${isFirstStep ? 0 : '-100%'})` }}
          >
            <CreateBoardSlideItem sx={{ padding: ' 32px 24px' }}>
              {isOpen &&
                (isNew ? (
                  <NewBoardForm
                    ref={formRef}
                    defaultCategory={defaultCategory}
                    isFormDirty={isFormDirty}
                    isDeploying={isDeploying}
                    setIsFormDirty={setIsFormDirty}
                    setDisabledSubmit={setDisabledSubmit}
                    setDisabledSave={setDisabledSave}
                    setDescription={setFooterDescription}
                    setFormType={setFormType}
                    options={options}
                    onSubmit={onSubmit}
                    handleClose={close}
                  />
                ) : (
                  <EditBoardForm
                    ref={formRef}
                    documentId={documentId}
                    isFormDirty={isFormDirty}
                    setIsFormDirty={setIsFormDirty}
                    setDisabledSubmit={setDisabledSubmit}
                    setDescription={setFooterDescription}
                    setFormType={setFormType}
                    options={options}
                    onSubmit={onSubmit}
                    handleClose={close}
                  />
                ))}
            </CreateBoardSlideItem>
            <CreateBoardSlideItem ref={previewScrollRef}>
              {previewData && (
                <BoardDetail
                  data={previewData}
                  scrollRef={previewScrollRef}
                  isRowDirection={isRowDirection}
                  headerId={`preview-modal-board-detail-header`}
                  disabled={true}
                  className={'isModal'}
                  previewDeployUrl={previewUrl}
                  sx={{
                    '&.isModal': {
                      '#preview-modal-board-detail-header': {
                        left: 'initial',
                        transform: 'none',
                        borderRight: '1px solid var(--dk-line, #E7E9EC)',
                      },
                      '--board-detail-padding-X': '24px',
                      '.board-content-wrap': { paddingX: '24px' },
                      '&.isRowDirection': {
                        '.board-feedback-wrap': {
                          height: '100%',
                          minHeight: 'calc(100vh - 80px - 138px - 60px)',
                        },
                        '#board-feedback': {
                          marginTop: '60px',
                        },
                      },
                      '#board-detail-comment-input-wrapper': {
                        display: 'none',
                      },
                      '#comment-sort-menu-button': {
                        color: 'var(--dk-text, #000)', //비활성화 되는게 맞으나,미리보기로 스타일 맞추느라 임의 적용 [PORTAL-10821]
                      },
                    },
                  }}
                />
              )}
            </CreateBoardSlideItem>
          </CreateBoardSlide>
        </>
      </Modal>
    );
  },
);
CreateBoardModal.displayName = 'CreateBoardModal';

export default CreateBoardModal;
